import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from './ui/Modal';
import Button from './ui/Button';
import Select from './ui/Select';
import Textarea from './ui/Textarea';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import api from '../lib/api';
import { PhotoCapture } from './PhotoCapture';
import { todayForInput } from '../lib/dateUtils';
import {
  getTestOptionsForAssetType,
  getFailureOptionsForAssetType,
  isExitEmergencyAsset,
  isRcdAsset,
  isWorkshopAsset,
} from '../constants/AssetTestConfig';
import { CONDITION_RATINGS } from '../constants/conditionRatings';
import { validateRcdResult, DID_NOT_TRIP_DEFECT } from '../lib/testValidators';
import { buildRecordTestPayload, deriveWorkshopResult } from '../lib/recordTestPayload';
import { AssetDetailsEditBlock } from './AssetDetailsEditBlock';
import RemovedAssetConfirm from './RemovedAssetConfirm';
import RcdEvidenceFields from './recordTest/RcdEvidenceFields';
import WorkshopChecklistFields from './recordTest/WorkshopChecklistFields';
import ExitEmergencyFailureFields from './recordTest/ExitEmergencyFailureFields';
import GenericTestFields from './recordTest/GenericTestFields';
import { useSessionStore } from '../store/sessionStore';
import { useExistingTestHydration } from '../hooks/useExistingTestHydration';

/** Optional pre-fill when opening for a specific result (e.g. Fail from session). */
const getDefaultTestType = (testOptions, initialTestType) => {
  if (initialTestType && testOptions.some((o) => o.value === initialTestType)) return initialTestType;
  return testOptions[0]?.value ?? '';
};

export default function RecordTestModal({
  asset,
  onClose,
  initialResult,
  initialTestType,
  initialDefectsFound = '',
  initialTripTimeMs = '',
  // No default: the push-button check is a test a technician must actually
  // perform, so it starts unanswered and the save is blocked until they answer.
  initialPushButton = null,
  existingTestId = null,
  onRecordSuccess,
  sessionId = null,
  onAssetDetailsUpdated = null,
}) {
  const queryClient = useQueryClient();
  // Group timers (for stamping the real discharge start on a 90-min result).
  const groupTimers = useSessionStore((s) => s.groupTimers);
  const storeSiteId = useSessionStore((s) => s.siteId);
  const testOptions = useMemo(
    () => (asset ? getTestOptionsForAssetType(asset.asset_type) : []),
    [asset?.asset_type]
  );
  const failureOptions = useMemo(
    () => (asset ? getFailureOptionsForAssetType(asset.asset_type) : []),
    [asset?.asset_type]
  );

  const [photoUrl, setPhotoUrl] = useState(null);
  const [conditionRating, setConditionRating] = useState(
    asset?.condition_rating != null ? String(asset.condition_rating) : ''
  );
  const [checklistValues, setChecklistValues] = useState({});
  const [showExitEmergencyFailReasons, setShowExitEmergencyFailReasons] = useState(initialResult === 'fail');
  // Set when an existing exit/emergency record's stored text can't be matched
  // to exactly one dropdown option (ambiguous multi-reason text, or text this
  // dropdown can't represent) — shown read-only so the edit is never blind to
  // what was actually recorded. See useExistingTestHydration and Brief 36 Part 3.
  const [exitEmergencyOriginalText, setExitEmergencyOriginalText] = useState(null);
  // RCD equivalent, after stripping measurement-derived reasons — Brief 37 Part 4.
  const [rcdOriginalText, setRcdOriginalText] = useState(null);

  const updateConditionMutation = useMutation(
    ({ id, rating, deferStatus }) =>
      api
        .put(`/assets/${id}`, {
          conditionRating: parseInt(rating, 10),
          ...(deferStatus ? { deferStatus: true } : {}),
        })
        .then((res) => res.data.asset),
    {
      onSuccess: (updated) => {
        queryClient.invalidateQueries(['assets']);
        onAssetDetailsUpdated?.(updated);
      },
      onError: (err) => {
        const msg = err.response?.data?.error || err.response?.data?.message || 'Could not update condition';
        toast.error(msg);
      },
    }
  );

  const { data: existingTest, isLoading: existingTestLoading, error: existingTestError } = useQuery(
    ['test', existingTestId],
    () => api.get(`/tests/${existingTestId}`).then((res) => res.data),
    {
      enabled: !!existingTestId,
      onSuccess: (data) => {
        // Handle photo URL from various DB naming conventions
        if (data?.photo_url != null && data.photo_url !== '') {
          setPhotoUrl(data.photo_url);
        } else if (data?.photoUrl != null && data.photoUrl !== '') {
          setPhotoUrl(data.photoUrl);
        } else {
          setPhotoUrl(null);
        }
      },
    }
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      testType: getDefaultTestType(testOptions, initialTestType),
      result: initialResult ?? 'pass',
      commentsRecommendations: initialDefectsFound || '',
      tripTimeMs: '',
      failureReason: '',
      // Push button starts unset — it is a test a technician must actually
      // perform, not an answer to pre-fill.
      rcdPushButton: initialPushButton === 'pass' || initialPushButton === 'fail' ? initialPushButton : '',
      rcdTripTimeMs: initialTripTimeMs !== '' && initialTripTimeMs != null ? String(initialTripTimeMs) : '',
      rcdFailureReason: '',
      photoUrl: null,
    },
  });

  const testType = watch('testType');
  const result = watch('result');
  const failureReason = watch('failureReason');

  // RCD Watchers
  const rcdPushButton = watch('rcdPushButton');
  const rcdTripTimeMs = watch('rcdTripTimeMs');
  const rcdFailureReason = watch('rcdFailureReason');

  const selectedOption = testOptions.find((o) => o.value === testType);
  const showMetrics = selectedOption?.requiresMetrics === true;
  const metricsLabel = selectedOption?.metricsLabel ?? 'Trip Time (ms)';
  const isTripTimeFailureReason = failureReason === 'Trip Time Failure (>300ms)';
  const tripTimeStrictRequired = showMetrics && isTripTimeFailureReason;

  const isExitEmergency = asset && isExitEmergencyAsset(asset.asset_type);
  const isRcd = asset && isRcdAsset(asset.asset_type);
  const isWorkshop = asset && isWorkshopAsset(asset.asset_type);
  const isGeneric = !isExitEmergency && !isRcd && !isWorkshop;
  // Condition 6 = Removed / Disconnected: no pass/fail is recorded; the asset's
  // status flip to 'removed' is deferred until the session finishes.
  const isRemoved = conditionRating === '6';
  const workshopResult = isWorkshop ? deriveWorkshopResult(checklistValues) : null;
  // RCD: the push-button check is a required test (Brief 37 dropped the
  // visual gate). Until it's answered, the overall result is genuinely
  // unknown, so show a pending state (mirroring the workshop checklist
  // below) instead of asserting a pass or a fail on the tech's behalf.
  const rcdPushAnswered = rcdPushButton === 'pass' || rcdPushButton === 'fail';
  const isRcdResultPending = isRcd && !rcdPushAnswered;
  // Selecting a failure reason forces the result to Fail — an exit/emergency
  // light with a reason recorded must never save as a Pass (Brief 36 Part 1).
  const hasExitEmergencyReason = isExitEmergency && !!(failureReason ?? '').trim();
  const effectiveResult = isWorkshop
    ? (workshopResult ?? result ?? 'pass')
    : isExitEmergency
      ? (hasExitEmergencyReason ? 'fail' : 'pass')
      : (result ?? 'pass');
  const effectiveResultLabel = effectiveResult === 'fail' ? 'FAIL' : 'PASS';
  const modalTitle = existingTestId ? `Editing ${effectiveResultLabel} result` : 'Record Test';
  const submitIdleLabel = (isWorkshop && workshopResult == null) || isRcdResultPending
    ? (existingTestId ? 'Update Test' : 'Record Test')
    : (existingTestId ? `Update ${effectiveResult === 'fail' ? 'Fail' : 'Pass'} result` : `Record ${effectiveResult === 'fail' ? 'Fail' : 'Pass'} result`);

  const clearExitEmergencyReasons = () => {
    setValue('failureReason', '');
    setExitEmergencyOriginalText(null);
    setShowExitEmergencyFailReasons(false);
  };

  // --- Auto-Calculate Result for Exit/Emergency ---
  useEffect(() => {
    if (!isExitEmergency) return;
    setValue('result', hasExitEmergencyReason ? 'fail' : 'pass');
  }, [isExitEmergency, hasExitEmergencyReason, setValue]);

  useEffect(() => {
    if (!isExitEmergency) return;
    if (hasExitEmergencyReason) {
      setShowExitEmergencyFailReasons(true);
    }
  }, [isExitEmergency, hasExitEmergencyReason]);

  // --- Auto-Calculate Result for RCD ---
  // Brief 37: pass requires the push button to pass, a trip time to be
  // measured, that time to be within the limit, AND no failure reason
  // selected. Anything else is a fail.
  useEffect(() => {
    if (!isRcd) return;

    const didNotTrip = rcdFailureReason === DID_NOT_TRIP_DEFECT;
    const hasReason = !!(rcdFailureReason ?? '').trim();

    // 1. Push button unanswered? -> leave the result alone. Forcing a fail
    // here would read as a verdict the tech never gave; the save is blocked
    // until they answer, so nothing can be recorded from this pending state.
    if (rcdPushButton !== 'pass' && rcdPushButton !== 'fail') {
      return;
    }

    // 2. Push Button Failure? -> Fail
    if (rcdPushButton !== 'pass') {
      setValue('result', 'fail');
      return;
    }

    // 3. Did not trip? -> Fail (no trip-time number is recorded)
    if (didNotTrip) {
      setValue('result', 'fail');
      return;
    }

    // 4. Trip Time Check
    const tripStr = (rcdTripTimeMs ?? '').toString().trim();
    // If empty, we can't determine pass yet — wait for valid input rather
    // than forcing 'fail' just for an empty string (avoids flickering); a
    // save with no trip time is caught by validation.
    if (!tripStr) {
      return;
    }

    const validation = validateRcdResult(tripStr, 'pass');
    if (!validation.valid) {
      setValue('result', 'fail');
      return;
    }

    // 5. A selected reason always forces a fail, even when the measurements
    // alone would pass (e.g. "Poor Condition" with a compliant trip time).
    setValue('result', hasReason ? 'fail' : validation.result);
  }, [isRcd, rcdPushButton, rcdTripTimeMs, rcdFailureReason, setValue]);

  // --- Clear Trip Time if RCD Push Button Fails or Did Not Trip is selected ---
  useEffect(() => {
    if (!isRcd) return;
    const didNotTrip = rcdFailureReason === DID_NOT_TRIP_DEFECT;
    if (rcdPushButton === 'fail' || didNotTrip) {
      clearErrors('rcdTripTimeMs');
      setValue('rcdTripTimeMs', ''); // No measurement exists in these states — clearing avoids a stale/fabricated number.
    }
  }, [isRcd, rcdPushButton, rcdFailureReason, clearErrors, setValue]);

  // --- Load Existing Test Data ---
  useExistingTestHydration({
    existingTest,
    asset,
    reset,
    setValue,
    testOptions,
    initialTestType,
    getDefaultTestType,
    setPhotoUrl,
    setShowExitEmergencyFailReasons,
    setExitEmergencyOriginalText,
    setRcdOriginalText,
    setChecklistValues,
  });

  const recordMutation = useMutation(
    async ({ payload, pushButtonPayload = null }) => {
      const res = existingTestId && payload.result !== 'removed'
        ? await api.put(`/tests/${existingTestId}`, payload)
        : await api.post('/tests', payload);

      // An RCD's push-button check is its own test with its own compliance clock,
      // so it is persisted as its own row via the batch endpoint (which upserts on
      // session+asset+test_type, so re-saving or editing updates rather than
      // duplicating). Failing this must not discard the primary result the tech
      // just recorded, so it degrades to a warning.
      if (pushButtonPayload) {
        try {
          await api.post('/tests/batch', { tests: [pushButtonPayload] });
        } catch (err) {
          console.error('Push Button row failed', err);
          toast.error('Result saved, but the Push Button test could not be recorded.');
        }
      }
      return res;
    },
    {
      onSuccess: (res, variables) => {
        queryClient.invalidateQueries(['assets']);
        queryClient.invalidateQueries(['tests']);
        const testResult = res.data?.testResult;
        const testId = variables?.payload?.result === 'removed' ? testResult?.id : (existingTestId ?? testResult?.id);
        const resultValue = testResult?.result ?? res.data?.result;
        const tripTimeMs = testResult?.metrics?.trip_time_ms ?? null;
        
        if (testId != null && resultValue != null && typeof onRecordSuccess === 'function') {
          onRecordSuccess(asset.id, { testId, result: resultValue, tripTimeMs });
        }
        
        toast.success(existingTestId ? 'Result updated' : 'Test recorded');
        onClose();
      },
      onError: (err) => {
        const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to record test';
        toast.error(msg);
      },
    }
  );

  const onSubmit = async (data) => {
    const userText = (data.commentsRecommendations || '').trim();

    // --- CONDITION 6 (REMOVED / DISCONNECTED) ---
    // No pass/fail is recorded. Write condition_rating = 6 with deferStatus so the
    // asset stays 'active' (and testable) until the session finishes, then record a
    // dedicated 'removed' result. Only fires when the rating actually changed to 6.
    const originalRatingForRemoved = asset?.condition_rating != null ? String(asset.condition_rating) : '';
    if (isRemoved && conditionRating !== originalRatingForRemoved) {
      try {
        await updateConditionMutation.mutateAsync({ id: asset.id, rating: '6', deferStatus: true });
      } catch {
        // error already toasted in onError; abort the removed record so we don't
        // create an orphan 'removed' result without the condition write.
        return;
      }
      recordMutation.mutate({
        payload: {
          assetId: asset.id,
          ...(sessionId != null && String(sessionId).trim() !== '' ? { sessionId: String(sessionId).trim() } : {}),
          testDate: todayForInput(),
          testType: 'Condition Update',
          result: 'removed',
          notes: userText || undefined,
        },
      });
      return;
    }

    const { payload, pushButtonPayload, error } = buildRecordTestPayload({
      asset,
      data,
      existingTestId,
      existingTest,
      sessionId,
      checklistValues,
      photoUrl,
      groupTimers,
      storeSiteId,
      exitEmergencyOriginalText,
      rcdOriginalText,
    });

    if (error) {
      toast.error(error);
      return;
    }

    // If condition changed, update asset condition alongside the test
    const originalRating = asset?.condition_rating != null ? String(asset.condition_rating) : '';
    if (conditionRating && conditionRating !== originalRating) {
      try {
        await updateConditionMutation.mutateAsync({ id: asset.id, rating: conditionRating });
      } catch {
        // error already toasted in onError; proceed with the test save regardless
      }
    }

    recordMutation.mutate({ payload, pushButtonPayload });
  };

  if (!asset) return null;

  return (
    <Modal isOpen onClose={onClose} className="record-test-modal">
      <div className="record-test-modal-header">
          {isRemoved ? (
            <span className="result-badge">REMOVED</span>
          ) : isRcdResultPending ? (
            <span className="result-badge">PENDING</span>
          ) : (
            <span className={`result-badge ${effectiveResult === 'fail' ? 'result-badge-fail' : 'result-badge-pass'}`}>
              {effectiveResult === 'fail' ? 'FAILED' : 'PASSED'}
            </span>
          )}
          <h2>{isRemoved ? 'Mark as Removed' : modalTitle}</h2>
        </div>

        <AssetDetailsEditBlock asset={asset} onUpdated={onAssetDetailsUpdated} compact />

        {existingTestId && existingTestLoading ? (
          <p className="text-muted">Loading test…</p>
        ) : existingTestId && existingTestError ? (
          <p className="form-error">Failed to load existing test details.</p>
        ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          
          {/* ASSET CONDITION */}
          <Select
            id="record-test-condition"
            label="Asset Condition"
            value={conditionRating}
            onChange={(e) => setConditionRating(e.target.value)}
          >
            <option value="">— Not set —</option>
            {CONDITION_RATINGS.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </Select>

          {isRemoved ? (
            <RemovedAssetConfirm register={register} />
          ) : (
          <>
          {/* WORKSHOP EQUIPMENT: 7-point checklist (replaces Test Type + Result) */}
          {isWorkshop && (
            <WorkshopChecklistFields
              checklistValues={checklistValues}
              setChecklistValues={setChecklistValues}
              workshopResult={workshopResult}
            />
          )}

          {/* GENERIC TEST TYPE, METRICS, RESULT & FAIL DETAILS */}
          {isGeneric && (
            <GenericTestFields
              register={register}
              errors={errors}
              testOptions={testOptions}
              showMetrics={showMetrics}
              metricsLabel={metricsLabel}
              tripTimeStrictRequired={tripTimeStrictRequired}
              result={result}
              failureOptions={failureOptions}
            />
          )}

          {/* RCD SPECIFIC FIELDS */}
          {isRcd && (
            <RcdEvidenceFields
              register={register}
              errors={errors}
              rcdPushAnswered={rcdPushAnswered}
              rcdPushButton={rcdPushButton}
              rcdFailureReason={rcdFailureReason}
              failureOptions={failureOptions}
              rcdOriginalText={rcdOriginalText}
            />
          )}

          {/* EXIT LIGHT SPECIFIC FIELDS */}
          {isExitEmergency && (
            <ExitEmergencyFailureFields
              register={register}
              failureOptions={failureOptions}
              showFailReasons={showExitEmergencyFailReasons}
              onMarkAsPass={clearExitEmergencyReasons}
              onAddFailureDetails={() => setShowExitEmergencyFailReasons(true)}
              unmatchedOriginalText={exitEmergencyOriginalText}
            />
          )}

          <Textarea
            id="record-test-comments"
            label="Comments & recommendations"
            rows={4}
            placeholder="Optional: observations, quotes, follow-up, or extra detail for this test."
            {...register('commentsRecommendations')}
          />

          <input type="hidden" {...register('photoUrl')} />
          <PhotoCapture
            currentUrl={photoUrl ?? watch('photoUrl') ?? ''}
            onUploadComplete={(url) => {
              setPhotoUrl(url ?? null);
              setValue('photoUrl', url ?? null);
            }}
            label="Photo evidence"
          />
          </>
          )}

          <div className="form-actions">
            <Button type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={recordMutation.isLoading || (!isRemoved && isWorkshop && workshopResult === null)}
            >
              {recordMutation.isLoading
                ? (existingTestId ? 'Updating…' : 'Recording…')
                : isRemoved ? 'Mark as Removed' : submitIdleLabel}
            </Button>
          </div>
        </form>
        )}
    </Modal>
  );
}